import type { Control } from "../core/Control.js";
import type { Size } from "../../common/geometry.js";
import { LayoutPanel, type LayoutPanelOptions } from "./LayoutPanel.js";

export interface WrapPanelOptions extends LayoutPanelOptions {
  /** Gap between lines, across the flow axis. Defaults to `spacing`. */
  lineSpacing?: number;
  /** Force every cell to this width, ignoring the child's own. */
  itemWidth?: number | null;
  /** Force every cell to this height. */
  itemHeight?: number | null;
}

interface Cell {
  control: Control;
  /** Extent along the flow axis, margin included - what the line has to find room for. */
  main: number;
  /** Extent across it, margin included. */
  cross: number;
  /** The control's own size, without its margin. */
  mainSize: number;
  crossSize: number;
}

interface Line {
  items: Cell[];
  main: number;
  cross: number;
}

/**
 * Lays children out in a line that wraps, horizontally by default.
 *
 * With `itemWidth` / `itemHeight` set it becomes a uniform grid, which is the
 * usual way to build a toolbar, a palette or an inventory. Without them each
 * child keeps its own size and the line breaks whenever the next one would not
 * fit.
 *
 * ```ts
 * const bag = new WrapPanel({ itemWidth: 48, itemHeight: 48, spacing: 4, autoSize: true });
 * for (const item of inventory) bag.controls.add(new ImageControl({ source: item.icon }));
 * ```
 */
export class WrapPanel extends LayoutPanel {
  lineSpacing: number;
  itemWidth: number | null;
  itemHeight: number | null;

  constructor(options: WrapPanelOptions = {}) {
    super(options);
    // WPF's WrapPanel flows horizontally by default; LayoutPanel's own default
    // is vertical because that is what a StackPanel wants.
    this.orientation = options.orientation ?? "horizontal";
    this.lineSpacing = options.lineSpacing ?? options.spacing ?? 0;
    this.itemWidth = options.itemWidth ?? null;
    this.itemHeight = options.itemHeight ?? null;
    this.performLayout();
  }

  /**
   * Break the children into lines that fit `available`, measuring nothing twice.
   *
   * A line's cross size is only known once the line is complete, and `align` positions items
   * against it, so both the arranging and the measuring start here.
   */
  #lines(available: Size): Line[] {
    const children = this.laidOutChildren();
    if (children.length === 0) return [];

    const horizontal = this.horizontal;
    const lineLimit = horizontal ? available.width : available.height;
    const crossLimit = horizontal ? available.height : available.width;

    const cells: Cell[] = children.map((c) => {
      const m = this.marginOf(c);
      const offer = horizontal
        ? { width: lineLimit, height: Math.max(0, crossLimit - m.crossBefore - m.crossAfter) }
        : { width: Math.max(0, crossLimit - m.crossBefore - m.crossAfter), height: lineLimit };
      const n = this.naturalSize(c, offer);
      const mainSize = horizontal ? (this.itemWidth ?? n.width) : (this.itemHeight ?? n.height);
      const crossSize = horizontal ? (this.itemHeight ?? n.height) : (this.itemWidth ?? n.width);
      return {
        control: c,
        // Line breaking measures the room a child needs, and a margin is part of that.
        main: mainSize + m.mainBefore + m.mainAfter,
        cross: crossSize + m.crossBefore + m.crossAfter,
        mainSize,
        crossSize,
      };
    });

    const lines: Line[] = [];
    let items: Cell[] = [];
    let mainUsed = 0;
    let crossUsed = 0;
    for (const cell of cells) {
      const advance = items.length > 0 ? this.spacing + cell.main : cell.main;
      if (items.length > 0 && mainUsed + advance > lineLimit + 0.5) {
        lines.push({ items, main: mainUsed, cross: crossUsed });
        items = [];
        mainUsed = 0;
        crossUsed = 0;
      }
      mainUsed += items.length > 0 ? this.spacing + cell.main : cell.main;
      crossUsed = Math.max(crossUsed, cell.cross);
      items.push(cell);
    }
    if (items.length > 0) lines.push({ items, main: mainUsed, cross: crossUsed });
    return lines;
  }

  protected override arrange(available: Size): Size {
    const lines = this.#lines(available);
    if (lines.length === 0) return { width: 0, height: 0 };
    const horizontal = this.horizontal;

    let crossOffset = 0;
    let widestLine = 0;
    for (const line of lines) {
      let mainOffset = 0;
      for (const cell of line.items) {
        const m = this.marginOf(cell.control);
        const place = this.crossPlace(
          line.cross,
          cell.crossSize,
          m.crossBefore,
          m.crossAfter,
          this.crossAlignOf(cell.control),
        );
        const mainStart = mainOffset + m.mainBefore;
        if (horizontal) {
          cell.control.setBounds(mainStart, crossOffset + place.offset, cell.mainSize, place.size);
        } else {
          cell.control.setBounds(crossOffset + place.offset, mainStart, place.size, cell.mainSize);
        }
        mainOffset += cell.main + this.spacing;
      }
      widestLine = Math.max(widestLine, line.main);
      crossOffset += line.cross + this.lineSpacing;
    }

    const crossExtent = Math.max(0, crossOffset - this.lineSpacing);
    return horizontal
      ? { width: widestLine, height: crossExtent }
      : { width: crossExtent, height: widestLine };
  }

  /**
   * How tall the flow gets in `available` — or how wide, flowing vertically.
   *
   * The flow axis is the one the caller already fixed by asking, so the answer along it is the
   * widest line rather than the room offered: a wrap panel given more room than it needs should
   * report the room it used.
   */
  protected override measure(available: Size): Size {
    const lines = this.#lines(available);
    if (lines.length === 0) return { width: 0, height: 0 };

    let widestLine = 0;
    let crossExtent = 0;
    for (const line of lines) {
      widestLine = Math.max(widestLine, line.main);
      crossExtent += line.cross + this.lineSpacing;
    }
    crossExtent = Math.max(0, crossExtent - this.lineSpacing);

    return this.horizontal
      ? { width: widestLine, height: crossExtent }
      : { width: crossExtent, height: widestLine };
  }

  /** Wrapping fixes the flow axis, so auto-sizing grows the other one. */
  protected override applyAutoSize(content: Size): void {
    const pad = this.padding;
    if (this.horizontal) this.setSize(this.width, content.height + pad.top + pad.bottom);
    else this.setSize(content.width + pad.left + pad.right, this.height);
  }

  /** Number of lines the current content breaks into. */
  get lineCount(): number {
    const children = this.laidOutChildren();
    if (children.length === 0) return 0;
    const horizontal = this.horizontal;
    const limit = horizontal ? this.clientSize.width : this.clientSize.height;
    let lines = 1;
    let used = 0;
    for (const c of children) {
      const n = this.naturalSize(c);
      const main = horizontal ? (this.itemWidth ?? n.width) : (this.itemHeight ?? n.height);
      const advance = used > 0 ? this.spacing + main : main;
      if (used > 0 && used + advance > limit + 0.5) {
        lines++;
        used = main;
      } else {
        used += advance;
      }
    }
    return lines;
  }
}
